<template>
  <div class="type-picker-root">
    <div
      v-if="multiSelect"
      ref="multiTriggerEl"
      class="type-picker-field"
      :class="{ 'type-picker-field--open': isDisplayed }"
      role="button"
      tabindex="0"
      @mousedown.stop
      @click="toggleMultiPicker"
      @keydown.enter.prevent="toggleMultiPicker"
      @keydown.space.prevent="toggleMultiPicker"
      @keydown.escape.stop="closePicker"
    >
      <div class="type-picker-values">
        <div
          v-for="entry in selectedEntries"
          :key="entry.id"
          class="type-picker-value"
          @mouseenter="showSelectedTooltip($event, entry)"
          @mouseleave="hideSelectedTooltip"
        >
          <span v-if="typeof entry.icon === 'string'" class="type-emoji">{{
            entry.icon
          }}</span>
          <component
            :is="entry.icon"
            v-else-if="entry.icon"
            class="type-icon"
          />
          <CubeIcon v-else class="type-icon" />
          <span class="type-picker-value-name">{{ labelFor(entry) }}</span>
          <button
            type="button"
            class="type-picker-value-remove"
            aria-label="Remove"
            @mousedown.stop.prevent
            @click.stop="removeSelection(entry.id)"
          >
            ×
          </button>
        </div>
        <span
          v-if="selectedEntries.length === 0"
          class="type-picker-placeholder"
        >
          {{ inlinePlaceholder }}
        </span>
      </div>
      <ChevronDownIcon class="type-picker-chevron" />
    </div>

    <Transition name="popup">
      <div
        v-show="isDisplayed"
        ref="pickerEl"
        class="type-picker"
        tabindex="0"
        :style="computedStyle"
        @mousedown.stop
        @click.stop
        @keydown.down.prevent.stop="navigate(1)"
        @keydown.up.prevent.stop="navigate(-1)"
        @keydown.enter.prevent.stop="selectCurrent"
        @keydown.escape.stop="closePicker"
      >
        <div class="type-input">
          <input
            ref="inputEl"
            v-model="queryText"
            type="text"
            class="type-search"
            :placeholder="searchPlaceholder"
          />
        </div>
        <div v-if="filtered.length" class="type-results">
          <template
            v-for="(section, sectionIndex) in sections"
            :key="section.key"
          >
            <div v-if="sectionIndex > 0" class="type-separator" />
            <div class="type-org">
              <div
                v-if="section.name"
                class="type-section type-section--level-1"
              >
                {{ section.name }}
              </div>
              <div
                v-for="workspace in section.workspaces"
                :key="workspace.key"
                class="type-workspace"
                :class="{
                  'type-workspace--collapsed': section.name && !workspace.name,
                }"
              >
                <div
                  v-if="workspace.name"
                  class="type-section type-section--level-2"
                >
                  {{ workspace.name }}
                </div>
                <div
                  v-for="item in workspace.entries"
                  :key="item.entry.id"
                  class="type-result"
                  :class="{
                    selected: item.index === selected,
                    'type-result--grouped': item.grouped,
                  }"
                  @mouseover="selected = item.index"
                  @click="select(item.entry)"
                >
                  <span
                    v-if="typeof item.entry.icon === 'string'"
                    class="type-emoji"
                    >{{ item.entry.icon }}</span
                  >
                  <component
                    :is="item.entry.icon"
                    v-else-if="item.entry.icon"
                    class="type-icon"
                  />
                  <CubeIcon v-else class="type-icon" />
                  <span class="type-name">{{ labelFor(item.entry) }}</span>
                  <CheckmarkIcon
                    v-if="isSelected(item.entry.id)"
                    class="type-checkmark"
                  />
                  <span
                    v-else-if="onDatabaseOpen"
                    class="type-database"
                    aria-label="Open database"
                    @mouseenter="showOpenDatabaseTooltip"
                    @mouseleave="hideOpenDatabaseTooltip"
                    @click.stop="openDatabase(item.entry)"
                  >
                    <DatabaseIcon :size="14" weight="light" />
                  </span>
                </div>
              </div>
            </div>
          </template>
        </div>
        <div v-else class="type-empty">No results</div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  ref,
  type Component,
  type PropType,
} from "vue";

import type { GraphDatabase } from "@ctrl/controller-rpc";

import {
  getDatabaseTitle,
  getTypeIcon,
  isUserDefinedDatabase,
} from "@/components/databases";
import CheckmarkIcon from "@/components/icons/CheckmarkIcon.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import CubeIcon from "@/components/icons/CubeIcon.vue";
import DatabaseIcon from "@/components/icons/DatabaseIcon.vue";
import * as inject from "@/components/inject";
import { defaultTableIds, definitiveTableId } from "@/components/tables";
import { useTooltip } from "@/components/tooltip";
import { isEllipsisActive } from "@/components/utils";
import { isWorkspaceWritable } from "@/composables/useProfile";
import { useTypeDatabasePicker } from "@/composables/useTypeDatabasePicker";
import { useWorkspaceDatabaseGroups } from "@/composables/useWorkspaceDatabaseGroups";
import { openPopoverCount } from "@/plugins/tippy-popover-count";

// One selectable type: a default type (Task/Event/Page) or a custom type.
export type TypeDatabasePickerEntry = {
  id: string;
  label: string;
  // A Vue icon component, an emoji string (custom types), or null for the
  // generic type icon.
  icon: Component | string | null;
  database: GraphDatabase;
  // Where the type/database lives, for organization/workspace sections.
  group?: {
    orgId: string;
    orgName: string;
    workspaceId: string;
    workspaceName: string;
  };
};

// The rendered list, nested so section headers can stick to the top of the
// scroll container while their own section scrolls: organizations contain
// workspaces, which contain the selectable entries. Default types
// (Task/Event/Page) form an initial unnamed section. `index` refers to the
// entry's position in `filtered`, which drives keyboard navigation.
type DisplayEntry = {
  entry: TypeDatabasePickerEntry;
  index: number;
  grouped: boolean;
};
type DisplayWorkspace = {
  key: string;
  name: string | null;
  entries: DisplayEntry[];
};
type DisplaySection = {
  key: string;
  name: string | null;
  workspaces: DisplayWorkspace[];
};

// Invoked exactly once: with the chosen type on selection, or `null` when the
// picker is dismissed without a choice.
type SelectCallback = (database: GraphDatabase | null) => void;

export type TypeDatabasePickerPlacement =
  | "bottom-start"
  | "right-start"
  | "left-start";

export type TypeDatabasePickerOptions = {
  // Where the popup opens relative to the trigger. "bottom-start" (default)
  // fits pill/dropdown anchors; "right-start" / "left-start" fit sidebar
  // entries (callers typically pass `placementForDir("right-start")`).
  placement?: TypeDatabasePickerPlacement;
  // Search input placeholder; defaults to the switch flow's "Switch type…".
  placeholder?: string;
  // When set, each row shows a trailing database icon; clicking it dismisses
  // the picker (reporting "no selection") and invokes this callback instead.
  onDatabaseOpen?: (database: GraphDatabase) => void;
};

const VIEWPORT_MARGIN = 4;
const DEFAULT_PLACEHOLDER = "Switch type…";
export type TypeDatabasePickerMode = "type" | "database";

const DATABASE_DEFAULT_KINDS = new Set(["event", "task", "page", "people"]);

const isSelectableDatabase = (database: GraphDatabase): boolean => {
  const defaultKind = defaultTableIds.get(definitiveTableId(database.id));
  return (
    (defaultKind !== undefined && DATABASE_DEFAULT_KINDS.has(defaultKind)) ||
    isUserDefinedDatabase(database) ||
    database.origin === "integration_app"
  );
};

export const databasePickerId = (database: GraphDatabase): string =>
  `${database.workspace}:${definitiveTableId(database.id)}`;

export default defineComponent({
  name: "TypeDatabasePicker",
  components: {
    CheckmarkIcon,
    ChevronDownIcon,
    CubeIcon,
    DatabaseIcon,
  },
  props: {
    selectedIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    multiSelect: { type: Boolean, default: false },
    mode: {
      type: String as PropType<TypeDatabasePickerMode>,
      default: "type",
    },
    orgId: { type: String, default: undefined },
    workspaceId: { type: String, default: undefined },
    inlinePlaceholder: { type: String, default: "Select" },
    // Only offer databases whose workspace the user can write — for pickers
    // that create objects (e.g. the sidebar "+ New" menu), where a `reader`
    // member's databases must not appear.
    writableOnly: { type: Boolean, default: false },
  },
  emits: {
    "update:selectedIds": (_selectedIds: string[]) => true,
  },
  setup(props, { emit }) {
    const isInputFocused = inject.isInputFocused();
    const profile = inject.profile();
    const workspaceEntries = useWorkspaceDatabaseGroups();
    const buildTypeEntries =
      props.mode === "type" ? useTypeDatabasePicker() : () => [];

    const isDisplayed = ref(false);
    const pickerEl = ref<HTMLElement | null>(null);
    const inputEl = ref<HTMLInputElement | null>(null);
    const multiTriggerEl = ref<HTMLElement | null>(null);
    const computedStyle = ref({});
    const triggerEl = ref<HTMLElement | undefined>(undefined);

    const databaseEntries = computed<TypeDatabasePickerEntry[]>(() => {
      if (props.mode !== "database") return [];
      const entries: TypeDatabasePickerEntry[] = [];
      const seen = new Set<string>();
      for (const workspace of workspaceEntries.value) {
        const organization = profile.value.organizations.find(
          (candidate) => candidate.id === workspace.orgId
        );
        const group: NonNullable<TypeDatabasePickerEntry["group"]> = {
          orgId: workspace.orgId,
          orgName: organization?.name ?? "Organization",
          workspaceId: workspace.id,
          workspaceName: workspace.name,
        };
        for (const database of Object.values(workspace.databases)) {
          if (!isSelectableDatabase(database)) continue;
          const id = databasePickerId(database);
          if (seen.has(id)) continue;
          seen.add(id);
          entries.push({
            id,
            label: database.data_name || "Untitled",
            icon: getTypeIcon(profile.value, database),
            database,
            group,
          });
        }
      }
      return entries;
    });
    const groupForDatabase = (
      database: GraphDatabase
    ): TypeDatabasePickerEntry["group"] | undefined => {
      const workspace = workspaceEntries.value.find(
        (candidate) => candidate.id === database.workspace
      );
      if (!workspace) return undefined;
      const organization = profile.value.organizations.find(
        (candidate) => candidate.id === workspace.orgId
      );
      return {
        orgId: workspace.orgId,
        orgName: organization?.name ?? "Organization",
        workspaceId: workspace.id,
        workspaceName: workspace.name,
      };
    };
    const declarativeEntries = computed<TypeDatabasePickerEntry[]>(() => {
      const entries =
        props.mode === "database" ? databaseEntries.value : buildTypeEntries();
      return entries.flatMap((entry) => {
        const group = entry.group ?? groupForDatabase(entry.database);
        if (
          props.workspaceId &&
          entry.database.workspace !== props.workspaceId
        ) {
          return [];
        }
        if (props.orgId && group?.orgId !== props.orgId) return [];
        if (
          props.writableOnly &&
          !isWorkspaceWritable(profile.value, entry.database.workspace)
        ) {
          return [];
        }
        return group && !entry.group ? [{ ...entry, group }] : [entry];
      });
    });
    const activeEntries = declarativeEntries;
    const queryText = ref("");
    const selected = ref(0);
    const onSelect = ref<SelectCallback | null>(null);
    const placement = ref<TypeDatabasePickerPlacement>("bottom-start");
    const searchPlaceholder = ref(DEFAULT_PLACEHOLDER);
    const onDatabaseOpen = ref<((database: GraphDatabase) => void) | null>(
      null
    );
    // Ensures the callback fires exactly once per invocation (select or cancel).
    let settled = false;
    // Participate in the global open-popover count (like Tippy popovers do) so
    // shells that dismiss on background clicks (e.g. the dashboard window)
    // swallow the click that closes this picker instead of closing themselves.
    let counted = false;
    const setCounted = (open: boolean) => {
      if (open === counted) return;
      counted = open;
      openPopoverCount.value += open ? 1 : -1;
    };

    const labelFor = (entry: TypeDatabasePickerEntry): string =>
      props.mode === "database"
        ? getDatabaseTitle(entry.database)
        : entry.label;

    const selectedTooltipEl = ref<HTMLElement | undefined>(undefined);
    const selectedTooltipContent = ref<string | undefined>(undefined);
    const selectedTooltip = useTooltip(
      selectedTooltipEl,
      selectedTooltipContent,
      "top"
    );
    onBeforeUnmount(selectedTooltip.release);
    const showSelectedTooltip = (
      event: MouseEvent,
      entry: TypeDatabasePickerEntry
    ) => {
      const target =
        event.currentTarget instanceof HTMLElement
          ? event.currentTarget
          : undefined;
      const label = target?.querySelector<HTMLElement>(
        ".type-picker-value-name"
      );
      if (!target || !isEllipsisActive(label ?? null)) return;
      selectedTooltipEl.value = target;
      selectedTooltipContent.value = labelFor(entry);
      selectedTooltip.showTooltip();
    };
    const hideSelectedTooltip = () => selectedTooltip.hideTooltip();

    const openDatabaseTooltipEl = ref<HTMLElement | undefined>(undefined);
    const openDatabaseTooltipContent = ref("Open database");
    const openDatabaseTooltip = useTooltip(
      openDatabaseTooltipEl,
      openDatabaseTooltipContent,
      "top"
    );
    onBeforeUnmount(openDatabaseTooltip.release);
    const showOpenDatabaseTooltip = (event: MouseEvent) => {
      if (!(event.currentTarget instanceof HTMLElement)) return;
      openDatabaseTooltipEl.value = event.currentTarget;
      openDatabaseTooltip.showTooltip();
    };
    const hideOpenDatabaseTooltip = () => openDatabaseTooltip.hideTooltip();

    const selectedEntries = computed<TypeDatabasePickerEntry[]>(() => {
      const selectedIds = new Set(props.selectedIds);
      return activeEntries.value.filter((entry) => selectedIds.has(entry.id));
    });

    // Checkmarked entries: the picked set in multi-select mode. In
    // single-select mode `selectedIds` is display-only — it ticks the
    // caller's current selection (e.g. the console's active type).
    const isSelected = (id: string): boolean => props.selectedIds.includes(id);

    // Match against the type name, but also the organization and workspace
    // names so e.g. typing a workspace name lists all of its types.
    const filtered = computed<TypeDatabasePickerEntry[]>(() => {
      const needle = queryText.value.trim().toLowerCase();
      if (!needle) return activeEntries.value;
      return activeEntries.value.filter((entry) => {
        const haystacks = [
          labelFor(entry),
          entry.label,
          entry.group?.orgName,
          entry.group?.workspaceName,
        ];
        return haystacks.some((text) => text?.toLowerCase().includes(needle));
      });
    });

    // Group the filtered list into organization > workspace sections, with
    // default types (no group) in a leading unnamed section. Selection
    // indices keep referring to `filtered`, so keyboard navigation skips
    // headers naturally.
    const sections = computed<DisplaySection[]>(() => {
      const result: DisplaySection[] = [];
      const organizationIds = new Set<string>();
      const workspaceIdsByOrganization = new Map<string, Set<string>>();
      for (const entry of activeEntries.value) {
        const group = entry.group;
        if (!group) continue;
        organizationIds.add(group.orgId);
        const workspaceIds =
          workspaceIdsByOrganization.get(group.orgId) ?? new Set<string>();
        workspaceIds.add(group.workspaceId);
        workspaceIdsByOrganization.set(group.orgId, workspaceIds);
      }
      const showOrganizationNames = organizationIds.size > 1;
      filtered.value.forEach((entry, index) => {
        const group = entry.group;
        const showWorkspaceName =
          group !== undefined &&
          (workspaceIdsByOrganization.get(group.orgId)?.size ?? 0) > 1;
        const sectionKey = group ? `org:${group.orgId}` : "defaults";
        const workspaceKey = group ? `ws:${group.workspaceId}` : "defaults";
        let section = result[result.length - 1];
        if (!section || section.key !== sectionKey) {
          section = {
            key: sectionKey,
            name: showOrganizationNames ? (group?.orgName ?? null) : null,
            workspaces: [],
          };
          result.push(section);
        }
        let workspace = section.workspaces[section.workspaces.length - 1];
        if (!workspace || workspace.key !== workspaceKey) {
          workspace = {
            key: workspaceKey,
            name: showWorkspaceName ? (group?.workspaceName ?? null) : null,
            entries: [],
          };
          section.workspaces.push(workspace);
        }
        workspace.entries.push({
          entry,
          index,
          grouped: showOrganizationNames || showWorkspaceName,
        });
      });
      return result;
    });

    const navigate = (direction: number) => {
      if (!filtered.value.length) return;
      const next = selected.value + direction;
      selected.value = Math.max(0, Math.min(next, filtered.value.length - 1));
    };

    const settle = (database: GraphDatabase | null) => {
      if (settled) return;
      settled = true;
      onSelect.value?.(database);
    };

    const removeSelection = (id: string) => {
      emit(
        "update:selectedIds",
        props.selectedIds.filter((value) => value !== id)
      );
    };

    const select = (entry: TypeDatabasePickerEntry) => {
      if (props.multiSelect) {
        emit(
          "update:selectedIds",
          isSelected(entry.id)
            ? props.selectedIds.filter((value) => value !== entry.id)
            : [...props.selectedIds, entry.id]
        );
        return;
      }
      settle(entry.database);
      hide();
    };

    // The trailing database icon: dismiss without selecting a type and let
    // the caller navigate to the entry's database instead.
    const openDatabase = (entry: TypeDatabasePickerEntry) => {
      const callback = onDatabaseOpen.value;
      settle(null);
      hide();
      callback?.(entry.database);
    };

    const selectCurrent = () => {
      const entry = filtered.value[selected.value];
      if (entry) select(entry);
    };

    const hide = () => {
      isDisplayed.value = false;
      isInputFocused.value = false;
      setCounted(false);
      document.removeEventListener("mousedown", handleClickOutside, {
        capture: true,
      });
    };

    // Public close (cancel): dismiss and report "no selection".
    const closePicker = () => {
      if (!props.multiSelect) settle(null);
      hide();
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (!isDisplayed.value) return;
      const target = event.target;
      const insidePicker =
        target instanceof Node && pickerEl.value?.contains(target);
      const insideTrigger =
        target instanceof Node && triggerEl.value?.contains(target);
      if (!insidePicker && !insideTrigger) {
        event.stopPropagation();
        event.preventDefault();
        closePicker();
      }
    };

    const updatePosition = () => {
      const anchor = triggerEl.value;
      if (!anchor || !document.body.contains(anchor)) return;
      const rect = anchor.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const pickerHeight = pickerEl.value?.offsetHeight || 0;
      const pickerWidth = pickerEl.value?.offsetWidth || 240;
      if (
        placement.value === "right-start" ||
        placement.value === "left-start"
      ) {
        // Beside the trigger, top-aligned. Prefer the requested side; flip
        // when it lacks room, and clamp vertically into the viewport.
        const fitsRight =
          rect.right + pickerWidth + VIEWPORT_MARGIN * 2 <= viewportWidth;
        const fitsLeft =
          rect.left - pickerWidth - VIEWPORT_MARGIN >= VIEWPORT_MARGIN;
        const preferRight = placement.value === "right-start";
        const placeRight = preferRight
          ? fitsRight || !fitsLeft
          : !(fitsLeft || !fitsRight);
        const left = Math.max(
          VIEWPORT_MARGIN,
          placeRight
            ? rect.right + VIEWPORT_MARGIN
            : rect.left - pickerWidth - VIEWPORT_MARGIN
        );
        const top = Math.max(
          VIEWPORT_MARGIN,
          Math.min(rect.top, viewportHeight - pickerHeight - VIEWPORT_MARGIN)
        );
        computedStyle.value = {
          position: "fixed",
          top: `${top}px`,
          left: `${left}px`,
          zIndex: 1000,
        };
        return;
      }
      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(rect.left, viewportWidth - pickerWidth - VIEWPORT_MARGIN)
      );
      const placeBelow =
        viewportHeight - rect.bottom >= pickerHeight + VIEWPORT_MARGIN ||
        rect.top < pickerHeight + VIEWPORT_MARGIN;
      computedStyle.value = {
        position: "fixed",
        ...(placeBelow
          ? { top: `${rect.bottom + VIEWPORT_MARGIN}px` }
          : { bottom: `${viewportHeight - rect.top + VIEWPORT_MARGIN}px` }),
        left: `${left}px`,
        zIndex: 1000,
      };
    };

    const openPicker = async () => {
      queryText.value = "";
      selected.value = 0;
      isDisplayed.value = true;
      isInputFocused.value = true;
      setCounted(true);
      await nextTick();
      updatePosition();
      document.addEventListener("mousedown", handleClickOutside, {
        capture: true,
      });
      nextTick(() => {
        pickerEl.value?.focus();
        inputEl.value?.focus();
      });
    };

    const showPicker = async (
      element: HTMLElement | undefined,
      callback: SelectCallback,
      options: TypeDatabasePickerOptions = {}
    ) => {
      triggerEl.value = element;
      onSelect.value = callback;
      placement.value = options.placement ?? "bottom-start";
      searchPlaceholder.value = options.placeholder ?? DEFAULT_PLACEHOLDER;
      onDatabaseOpen.value = options.onDatabaseOpen ?? null;
      settled = false;
      await openPicker();
    };

    const toggleMultiPicker = async () => {
      if (isDisplayed.value) {
        closePicker();
        return;
      }
      const element = multiTriggerEl.value;
      triggerEl.value = element ?? undefined;
      onSelect.value = null;
      placement.value = "bottom-start";
      searchPlaceholder.value =
        props.mode === "database" ? "Search databases…" : "Search types…";
      onDatabaseOpen.value = null;
      settled = false;
      await openPicker();
    };

    onBeforeUnmount(() => {
      setCounted(false);
      document.removeEventListener("mousedown", handleClickOutside, {
        capture: true,
      });
    });

    return {
      closePicker,
      computedStyle,
      sections,
      filtered,
      hideOpenDatabaseTooltip,
      inputEl,
      isDisplayed,
      isSelected,
      labelFor,
      multiTriggerEl,
      navigate,
      onDatabaseOpen,
      openDatabase,
      pickerEl,
      queryText,
      removeSelection,
      searchPlaceholder,
      select,
      selectCurrent,
      selected,
      selectedEntries,
      showOpenDatabaseTooltip,
      showSelectedTooltip,
      showPicker,
      hideSelectedTooltip,
      toggleMultiPicker,
    };
  },
});
</script>

<style scoped lang="scss">
@include popup-transition();

.type-picker-root {
  display: contents;
}

.type-picker-field {
  display: flex;
  align-items: center;
  min-width: 180px;
  box-sizing: border-box;
  padding-block: 4px;
  padding-inline-end: 6px;
  padding-inline-start: 7px;
  border-radius: $border-radius-m;
  cursor: default;
  @include transition(border-color 100ms ease-out);
  @include themed() {
    background: t($bg);
    border: 1px solid t($grey-15);
    color: t($grey-80);
  }

  &:hover,
  &--open {
    @include themed() {
      border-color: t($grey-30);
    }
  }
}

.type-picker-values {
  display: flex;
  align-items: center;
  flex: 1;
  flex-wrap: nowrap;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}

.type-picker-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  padding-block: 1px;
  padding-inline-end: 4px;
  padding-inline-start: 6px;
  border-radius: $border-radius-m;
  flex: 0 1 auto;
  @extend %font-caption-regular;
  @include themed() {
    border: 1px solid t($grey-15);
    color: t($primary);
  }
}

.type-picker-value-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-picker-value-remove {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-50);
  }

  &:hover {
    @include themed() {
      color: t($primary);
    }
  }
}

.type-picker-placeholder {
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-40);
  }
}

.type-picker-chevron {
  width: 10px;
  height: 10px;
  margin-inline-start: 12px;
  flex-shrink: 0;
  @include themed() {
    color: t($grey-50);
  }
}

.type-checkmark {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  @include themed() {
    color: t($grey-50);
  }
}

.type-picker {
  position: fixed;
  border-radius: $border-radius-l;
  padding: 4px;
  width: 240px;
  transform-origin: top center;
  will-change: transform, opacity;
  @include themed() {
    background: t($bg-tooltip);
    border: 1px solid t($border);
    box-shadow: $tooltip;
    color: t($text);
  }
}

.type-input {
  .type-search {
    width: 100%;
    box-sizing: border-box;
    padding: 5px 10px;
    border-radius: $border-radius-m;
    @extend %font-caption-regular;
    @include themed() {
      background: t($bg);
      border: 1px solid t($grey-15);
      color: t($grey-80);
      &::placeholder {
        color: t($grey-50);
        @extend %font-caption-medium;
      }
      &:focus {
        background: t($grey-10);
        border: 1px solid t($grey-10);
      }
    }
  }
}

.type-results {
  // Top spacing as margin (not padding) so sticky headers pin flush to the
  // scrollport edge with no gap for rows to peek through.
  margin: 4px 0 0 0;
  max-height: 280px;
  overflow-y: auto;
}

// Header height: 8px + 2px vertical padding + 17px small-font line-height.
// The workspace header sticks right below the organization header.
$type-section-height: 27px;

.type-section {
  position: sticky;
  padding: 8px 8px 2px;
  cursor: default;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @extend %font-small-regular;
  @include themed() {
    color: t($grey-50);
    background: t($bg-tooltip);
  }

  &--level-1 {
    top: 0;
    z-index: 2;
  }

  &--level-2 {
    top: $type-section-height;
    z-index: 1;
    padding-inline-start: 16px;
  }
}

.type-separator {
  height: 1px;
  margin: 4px 8px;
  @include themed() {
    background: t($border);
  }
}

.type-result {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: $border-radius-m;
  cursor: default;
  @extend %font-caption-regular;

  &.selected {
    @include themed() {
      background: t($bg-menu-hover);
    }
  }

  // Grouped types sit under an organization > workspace hierarchy; indent
  // them one level deeper than the workspace header.
  &--grouped {
    padding-inline-start: 24px;
  }

  // In a collapsed workspace (personal org, no workspace header) the types
  // sit directly under the organization name, one indent level less.
  .type-workspace--collapsed & {
    padding-inline-start: 16px;
  }
}

.type-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;

  // Some kind icons (e.g. TaskIcon) wrap an intrinsically larger svg, so the
  // `.type-icon` box alone doesn't shrink them; force the inner svg to match.
  :deep(svg) {
    width: 14px;
    height: 14px;
  }
}

.type-emoji {
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
}

.type-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// The optional per-row "open the type's database" affordance, revealed on the
// hovered/selected row like the sidebar's database links.
.type-database {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px;
  border-radius: $border-radius-s;
  opacity: 0;
  @include transition(background 100ms ease-out, opacity 100ms ease-out);

  .type-result.selected & {
    opacity: 1;
  }

  &:hover {
    @include themed() {
      background: t($grey-15);
    }
  }
}

.type-empty {
  padding: 12px;
  text-align: center;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-50);
  }
}
</style>
